import { Close, FileDownload, Groups } from '@mui/icons-material';
import { Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import ModalV2 from '/@/components/bayer/modal-v2';
import { useActivityById, useActivityPdfDownload } from '/@/hooks/activities';
import { _ } from '/@/locale';
import { getActivityTypeLabel, getModalityLabel } from '../../constants';
import ActivityDetailContent from '../activity-detail-content';

const ActivityDetailModal = ({
    open,
    onClose,
    activityId,
}: {
    open: boolean;
    onClose: () => void;
    activityId?: number;
}) => {
    const { data: activity } = useActivityById(activityId, { enabled: open && Boolean(activityId) });
    const { download: handleDownload, isDownloading } = useActivityPdfDownload(activityId ?? 0);

    return (
        // MUI's Modal portals its content to document.body, but React's synthetic events still
        // bubble through the component tree — without this, any click here bubbles up into
        // ActivityCard's onClick and opens the edit form instead. Not an interactive element,
        // just an event-propagation boundary, so no keyboard handler is needed.
        // biome-ignore lint/a11y/useKeyWithClickEvents: non-interactive propagation boundary
        // biome-ignore lint/a11y/noStaticElementInteractions: non-interactive propagation boundary
        <div onClick={(e) => e.stopPropagation()}>
            <ModalV2
                open={open}
                onClose={onClose}
                maxWidth='sm'
                header={
                    <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ px: 2, py: 1 }}>
                        {activity && (
                            <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center' useFlexGap>
                                {[
                                    dayjs(activity.date).format('DD/MM/YY'),
                                    getActivityTypeLabel(activity.activityType),
                                ].map((part) => (
                                    <Typography
                                        key={part}
                                        color='primary.mui.inactive'
                                        fontWeight={500}
                                        variant='subtitle2'
                                        sx={{
                                            letterSpacing: '1.25px',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {part}
                                    </Typography>
                                ))}
                                <Typography
                                    color='primary.mui.inactive'
                                    fontWeight={500}
                                    variant='subtitle2'
                                    sx={{
                                        letterSpacing: '1.25px',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    |
                                </Typography>
                                <Stack direction='row' spacing={0.5} alignItems='center'>
                                    <Groups sx={{ width: 18, height: 18, color: 'primary.mui.inactive' }} />
                                    <Typography
                                        color='primary.mui.inactive'
                                        fontWeight={500}
                                        variant='subtitle2'
                                        sx={{
                                            letterSpacing: '1.25px',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {getModalityLabel(activity.modality)}
                                    </Typography>
                                </Stack>
                            </Stack>
                        )}
                        <IconButton onClick={onClose}>
                            <Close sx={{ width: 18, height: 18 }} />
                        </IconButton>
                    </Stack>
                }
                bodyProps={{
                    alignItems: 'stretch',
                    textAlign: 'left',
                    sx: { p: 3, maxHeight: '70vh', overflow: 'auto' },
                }}
                actions={
                    <Button
                        variant='contained'
                        color='secondary'
                        endIcon={
                            isDownloading ? (
                                <CircularProgress size={16} color='inherit' />
                            ) : (
                                <FileDownload sx={{ width: 18, height: 18 }} />
                            )
                        }
                        onClick={handleDownload}
                        disabled={!activity || isDownloading}
                    >
                        {isDownloading ? _('Generando PDF...') : _('Descargar')}
                    </Button>
                }
                actionsProps={{ justifyContent: 'flex-end' }}
            >
                {!activity ? (
                    <Stack alignItems='center' py={4}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <ActivityDetailContent activity={activity} />
                )}
            </ModalV2>
        </div>
    );
};

export default ActivityDetailModal;
